import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { allProperties, propertyTypes } from "../../mocks/properties";
import InterestPopup from "./components/InterestPopup";

export default function PropertiesPage() {
  const [activeType, setActiveType] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [wishlist, setWishlist] = useState<number[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only show once per session
    const alreadyShown = sessionStorage.getItem("interestPopupShown");
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      setShowPopup(true);
      sessionStorage.setItem("interestPopupShown", "true");
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  const toggleWishlist = (id: number) => {
    setWishlist((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const filtered = allProperties.filter((p) => {
    const matchType = activeType === "All" || p.type === activeType;
    const matchSearch =
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchSearch;
  });

  const parseValue = (price: string): number => {
    const num = parseFloat(price.replace(/[^0-9.]/g, ""));
    if (price.includes("M")) return num * 1_000_000;
    if (price.includes("K")) return num * 1_000;
    return num;
  };

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "price-asc") return parseValue(a.price) - parseValue(b.price);
    if (sortBy === "price-desc") return parseValue(b.price) - parseValue(a.price);
    return 0;
  });

  return (
    <main className="min-h-screen bg-[#EEF2F9]">
      {showPopup && <InterestPopup onClose={() => setShowPopup(false)} />}
      <Navbar />

      {/* Page Header */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 400,
          backgroundImage: "url('https://readdy.ai/api/search-image?query=aerial%20view%20of%20a%20collection%20of%20UK%20residential%20investment%20properties%2C%20neat%20rows%20of%20red%20brick%20two%20storey%20houses%20across%20Yorkshire%20and%20England%2C%20well%20maintained%20gardens%20and%20streets%2C%20warm%20golden%20afternoon%20sunlight%2C%20professional%20real%20estate%20investment%20photography%2C%20no%20people%2C%20drone%20wide%20shot%2C%20property%20portfolio%20view%2C%20investment%20grade%20residential%20assets%2C%20suburban%20British%20neighbourhood&width=1400&height=400&seq=201&orientation=landscape')",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(7,26,46,0.92) 0%, rgba(7,26,46,0.78) 55%, rgba(7,26,46,0.60) 100%)" }} />
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #C9A84C 25%, #E2C27A 55%, #C9A84C 80%, transparent)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-14">
          <span className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/35 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.18em] px-4 py-1.5 rounded-full mb-5">
            <i className="ri-building-2-line text-xs leading-none" />
            UK Social Housing Portfolio
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Underlying Assets
          </h1>
          <p className="text-white/65 text-sm max-w-xl mb-8">
            UK assisted living and supported housing properties forming the asset base of the Thorpe Dellow Social Housing Structured Product.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            {[
              { icon: "ri-building-4-line", value: "35", label: "Properties" },
              { icon: "ri-money-pound-circle-line", value: "£47M+", label: "Combined AUM" },
              { icon: "ri-home-2-line", value: "545+", label: "Residential Units" },
              { icon: "ri-map-pin-2-line", value: "3 Nations", label: "ENG · WAL · SCO" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center text-[#C9A84C]">
                  <i className={`${s.icon} text-sm leading-none`} />
                </div>
                <div>
                  <p className="text-white font-bold text-sm leading-none">{s.value}</p>
                  <p className="text-white/50 text-[10px] mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
            <button
              onClick={() => navigate("/brochure")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wide cursor-pointer whitespace-nowrap transition-all hover:brightness-110"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)", color: "#071A2E" }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-download-line leading-none" />
              </div>
              Download Brochure
            </button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4 flex flex-col gap-3">
          {/* Type Tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {propertyTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeType === type
                    ? "bg-[#071A2E] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3 w-full">
            {/* Search */}
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 flex-1 min-w-0">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-search-line text-slate-400 text-sm leading-none" />
              </div>
              <input
                type="text"
                placeholder="Search location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent text-sm text-slate-700 focus:outline-none placeholder-slate-400 w-full min-w-0"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-100 rounded-lg px-2 md:px-3 py-2 text-xs md:text-sm text-slate-600 focus:outline-none cursor-pointer flex-shrink-0"
            >
              <option value="default">Default</option>
              <option value="price-asc">Price ↑</option>
              <option value="price-desc">Price ↓</option>
            </select>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <p className="text-slate-400 text-sm mb-6">
          Showing <strong className="text-[#071A2E]">{sorted.length}</strong> property assets
        </p>

        {sorted.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
              <i className="ri-building-line text-5xl leading-none" />
            </div>
            <p className="text-sm">No properties found. Try a different filter or search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {sorted.map((p) => (
              <Link
                key={p.id}
                to={`/properties/${p.id}`}
                className="bg-white rounded-2xl overflow-hidden group cursor-pointer transition-transform hover:-translate-y-1 duration-300 block"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 bg-[#071A2E] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {p.tag}
                  </span>
                  <button
                    onClick={(e) => { e.preventDefault(); toggleWishlist(p.id); }}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 transition-colors cursor-pointer"
                  >
                    <i
                      className={`text-sm leading-none ${
                        wishlist.includes(p.id)
                          ? "ri-heart-fill text-red-500"
                          : "ri-heart-line text-slate-500 hover:text-red-500"
                      }`}
                    />
                  </button>
                </div>
                <div className="p-5">
                  <p className="text-xs text-slate-400 font-medium mb-1">{p.type}</p>
                  <h3
                    className="font-bold text-[#071A2E] text-base leading-snug mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {p.title}
                  </h3>
                  <div className="flex items-center gap-1.5 text-slate-400 text-xs mb-3">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line leading-none" />
                    </div>
                    <span>{p.location}</span>
                  </div>
                  <p className="text-[#C9A84C] font-bold text-lg mb-4">{p.price}</p>
                  <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-slate-400 text-xs">
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-home-2-line leading-none" />
                      </div>
                      {p.beds} Units
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-layout-line leading-none" />
                      </div>
                      {p.sqft.toLocaleString()} sqft
                    </span>
                    <span className="flex items-center gap-1 text-[#071A2E] font-medium">
                      View Details
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line leading-none" />
                      </div>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </main>
  );
}
